import { useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  CheckCircle2,
  Mail,
  Phone,
  Download,
  Home,
} from "lucide-react";

import { callProxy } from "../api/api";
import { TokenContext } from "../components/TokenContext";

export default function BookingSuccess() {
  const navigate = useNavigate();
  const { token } = useContext(TokenContext);

  const [verifying, setVerifying] = useState(true);
  const [paymentOk, setPaymentOk] = useState(false);
  const [bookingData, setBookingData] = useState(null);

  // ✅ VERIFY STRIPE PAYMENT USING SESSION_ID
  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");

    if (!sessionId) {
      navigate("/payment-failed");
      return;
    }

    const confirmPayment = async () => {
      try {
        const res = await callProxy(`/api/verify-payment/${sessionId}`, token);
        console.log("Stripe verify response:", res);

        // ✅ Stripe confirmed
        if (res?.data?.status === "complete" && res?.data?.payment === "paid") {

          setBookingData(res.data.booking);

          // ✅ Create booking in Smoobu AFTER payment verified
          // await callProxy("/api/create-booking-after-payment", token, {
          //   method: "POST",
          //   body: { bookingData: res.data.booking },
          // });

          setPaymentOk(true);

        } else {
          navigate("/payment-failed");
        }

      } catch (err) {
        console.error("Stripe verification failed:", err);
        navigate("/payment-failed");
      } finally {
        setVerifying(false);
      }
    };

    confirmPayment();
  }, [navigate, token]);

  // ✅ WAIT STATE
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg font-medium">Verifying payment...</p>
      </div>
    );
  }

  // ✅ BLOCK IF FAILED
  if (!paymentOk) return null;

  // ✅ BLOCK IF BOOKING DATA NOT READY
  if (!bookingData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg font-medium">Loading booking details...</p>
      </div>
    );
  }

  // ✅ SAFE DATA ACCESS
  const checkIn = bookingData.arrivalDate ? new Date(bookingData.arrivalDate) : null;
  const checkOut = bookingData.departureDate ? new Date(bookingData.departureDate) : null;

  const nights =
    checkIn && checkOut
      ? Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24))
      : 0;

  const guestName =
    `${bookingData.firstName || ""} ${bookingData.lastName || ""}`.trim() || "Guest";

  const guestEmail = bookingData.email || "Not provided";
  const guestPhone = bookingData.phone || "Not provided";
  const guestsCount = bookingData.adults || 1;

  const totalPrice = Number(bookingData.price || 0).toFixed(2);
  const apartmentName = `Apartment ID ${bookingData.apartmentId}`;

  // ✅ SHARING
  const whatsappText = encodeURIComponent(
    `Booking confirmed for ${apartmentName}
Dates: ${checkIn ? format(checkIn, "dd MMM yyyy") : ""} - ${checkOut ? format(checkOut, "dd MMM yyyy") : ""}
Guests: ${guestsCount}
Total: AED ${totalPrice}`
  );

  const emailBody = encodeURIComponent(
    `Booking confirmed

Property: ${apartmentName}
Dates: ${checkIn ? format(checkIn, "dd MMM yyyy") : ""} - ${checkOut ? format(checkOut, "dd MMM yyyy") : ""}
Guests: ${guestsCount}
Total: AED ${totalPrice}`
  );

  // ✅ RENDER
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100">
      <div className="max-w-5xl mx-auto px-4 py-8 md:py-12">

        {/* HEADER */}
        <div className="bg-white rounded-2xl shadow-md border border-emerald-100 p-6 mb-6 flex items-center gap-4">
          <CheckCircle2 className="w-8 h-8 text-emerald-500" />
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              Booking Confirmed!
            </h1>
            <p className="text-sm text-slate-600">
              Your payment was successful and your booking is complete.
            </p>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">

          {/* LEFT */}
          <div className="bg-white p-5 rounded-2xl shadow-sm">
            <h2 className="text-lg font-semibold mb-4">Stay details</h2>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-gray-500">Check-in</p>
                <p>{checkIn ? format(checkIn, "dd MMM yyyy") : "-"}</p>
              </div>

              <div>
                <p className="text-xs text-gray-500">Check-out</p>
                <p>{checkOut ? format(checkOut, "dd MMM yyyy") : "-"}</p>
              </div>

              <div>
                <p className="text-xs text-gray-500">Guests</p>
                <p>{guestsCount}</p>
              </div>

              <div>
                <p className="text-xs text-gray-500">Nights</p>
                <p>{nights}</p>
              </div>
            </div>

            <div className="mt-4 border-t pt-4 flex justify-between text-sm">
              <span>Total</span>
              <strong>AED {totalPrice}</strong>
            </div>
          </div>

          {/* RIGHT */}
          <div className="bg-white p-5 rounded-2xl shadow-sm">
            <h2 className="text-lg font-semibold mb-4">Guest details</h2>

            <p className="font-medium">{guestName}</p>

            <div className="flex items-center mt-2 gap-2">
              <Mail className="w-4 h-4" />
              {guestEmail}
            </div>

            <div className="flex items-center mt-2 gap-2">
              <Phone className="w-4 h-4" />
              {guestPhone}
            </div>
          </div>
        </div>

        {/* ACTION BAR */}
        <div className="mt-8 flex justify-between items-center">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 px-4 py-2 border rounded"
          >
            <Home className="w-4 h-4" />
            Home
          </button>

          <div className="flex gap-2">
            <a
              href={`https://wa.me/?text=${whatsappText}`}
              target="_blank"
              rel="noreferrer"
              className="px-4 py-2 rounded bg-green-500 text-white"
            >
              Share WhatsApp
            </a>

            <a
              href={`mailto:?body=${emailBody}`}
              className="px-4 py-2 rounded bg-gray-600 text-white"
            >
              Share Email
            </a>

            <button
              onClick={() => window.print()}
              className="px-4 py-2 rounded bg-blue-600 text-white"
            >
              <Download className="inline w-4 h-4 mr-1" />
              Print
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
